import Http from './axios-config';

class AppointmentsService {

    constructor() {
        this.http = new Http();
    }

    /**
     *
     * @param {*} date
     */
    async getAppointmentsSessionByDate(date, extraParams = {}) {
        return await this.http.get("/clinical-admins/appointments/sessions", {
            params: { date: date, ...extraParams }
        });
    }

    async createAppointmentsSession(session) {
        return await this.http.post("/clinical-admins/appointments/sessions", session);
    }

    async deleteAppointmentSession(id, values) {
        return await this.http.delete(`/clinical-admins/appointments/sessions/${id}`, {
            disableToast: true,
            data: values
        });
    }

    async getAppointmentsSlotsByDate(params) {
        return await this.http.get(`/clinical-admins/appointments/slots`, { params });
    }

    async blockUnblockAppointmentSlot(id) {
        return await this.http.patch(`/clinical-admins/appointments/slots/${id}/blocked`);
    }

    async setDNAStatus(id, status) {
        return await this.http.patch(`/clinical-admins/appointments/slots/${id}/status`, status);
    }

    async sendConsultation(id, notes) {
        return await this.http.post(`/clinical-admins/appointments/slots/${id}/consultations`, notes);
    }

    async editConsultation(id, notes) {
        return await this.http.put(`/clinical-admins/appointments/slots/${id}/consultations`, notes);
    }

    /**
     * Record the mental-health question for ONE consultation.
     *
     * The answer belongs to this consultation and no other: a patient may be recorded as not a
     * mental health consultation today and as one next month, and both stay true of the
     * consultation they belong to. The backend keys the row by appointment slot and rejects a plan
     * sent alongside an answer of "no", so send exactly what the clinician chose.
     *
     * @param {number} id appointment slot id
     * @param {{isMentalHealth: boolean, plan?: number, followUpDueAt?: string}} review
     */
    /**
     * Read the mental-health answer for ONE consultation.
     *
     * A 404 means the question was never answered for this consultation, which is NOT the same as
     * an answer of "no" - the backend stores those differently and the UI must not conflate them.
     *
     * @param {number} id appointment slot id
     */
    async getMentalHealthReview(id) {
        return await this.http.get(`/clinical-admins/appointments/slots/${id}/mental-health-review`, {
            disableToast: true,
        });
    }

    /**
     * Read this booking's PHQ-9 and GAD-7 scores.
     *
     * A 404 means the patient has not completed them, which is NOT a score of zero: a PHQ-9 of 0 is
     * a real and reassuring measurement and no measurement at all is not. The caller must render
     * those two states differently.
     *
     * disableToast, like the mental-health read beside it: "not completed yet" is the ordinary case
     * on most appointments and must not throw an error toast at the clinician for it.
     *
     * @param {number} id appointment slot id
     */
    async getQuestionnaires(id) {
        return await this.http.get(`/clinical-admins/appointments/slots/${id}/questionnaires`, {
            disableToast: true,
        });
    }

    /**
     * Stop this consultation's follow-up reminder before it is sent.
     *
     * The answer itself is kept - what was recorded, and that it was stopped, both stay on the
     * record. Only the pending email is cancelled.
     *
     * @param {number} id appointment slot id
     */
    async stopMentalHealthReminders(id) {
        return await this.http.delete(`/clinical-admins/appointments/slots/${id}/mental-health-review`);
    }

    async setMentalHealthReview(id, review) {
        return await this.http.put(`/clinical-admins/appointments/slots/${id}/mental-health-review`, review);
    }

    /**
     * Every change to this consultation's mental-health answer, newest first. Super admins only; the
     * card shows its own message when it cannot load, so no toast.
     *
     * @param {number} id appointment slot id
     */
    async getMentalHealthReviewChanges(id) {
        return await this.http.get(`/clinical-admins/appointments/slots/${id}/mental-health-review/changes`, {
            silentErrors: true,
        });
    }

    async getAppointmentComments(id) {
        return await this.http.get(`/clinical-admins/appointments/slots/${id}/comments`);
    }

    async addComment(id, comment) {
        return await this.http.post(`/clinical-admins/appointments/slots/${id}/comments`, comment);
    }

    async rescheduleAppointment(id, values) {
        return await this.http.put(`/clinical-admins/appointments/slots/${id}/schedule`, values);
    }

    async createAppointment(id, values) {
        return await this.http.put(`/clinical-admins/appointments/slots/${id}/patients`, values);
    }

    async deleteAppointment(id) {
        return await this.http.delete(`/clinical-admins/appointments/slots/${id}`);
    }

    async getAppointment(id) {
        return await this.http.get(`/clinical-admins/appointments/slots/${id}`);
    }

    async squeezeAppointmentSlot(id) {
        return await this.http.put(`/clinical-admins/appointments/slots/${id}/squeeze`);
    }

    async updateAppointmentActualStartsTime(id, time) {
        return await this.http.put(`/clinical-admins/appointments/slots/${id}/times`, { actualStartsAt: time });
    }

    async updateAppointmentActualEndsTime(id, time) {
        return await this.http.put(`/clinical-admins/appointments/slots/${id}/times`, { actualEndsAt: time });
    }

    async editAppointmentSession(id, values) {
        return await this.http.put(`/clinical-admins/appointments/sessions/${id}`, values);
    }

    /**
     * Move a whole session to another doctor (super admins and Admins; the server checks). Sends only the
     * new doctor and the doctor the page showed - if the session has another doctor by now, the server
     * refuses (409) rather than move it from someone else. No toast on a refusal: the move modal shows
     * the server's message itself.
     *
     * @param {number} id  the session
     * @param {{toDoctorId: number, expectedFromDoctorId: number}} body  doctors.id values
     */
    async moveAppointmentSession(id, { toDoctorId, expectedFromDoctorId }) {
        return await this.http.put(`/clinical-admins/appointments/sessions/${id}/doctor`, {
            toDoctorId,
            expectedFromDoctorId,
        }, { silentErrors: true });
    }

    async getSessionById(id) {
        return await this.http.get(`/clinical-admins/appointments/sessions/${id}`);
    }

    async sendTrustPilot(id) {
        return await this.http.post(`/clinical-admins/appointments/${id}/trust-pilot/email`);
    }

    async showHideConsultationNotes(id, isHidden) {
        return await this.http.patch(`/clinical-admins/appointments/slots/${id}/consultations/is-hide`, {
            isHidden
        });
    }

    async getTakenTimeSlots(params) {
        return await this.http.get("/clinical-admins/appointments/sessions/timeslots", { params });
    }

    async getTakenPriorities(params) {
        return await this.http.get("/clinical-admins/appointments/sessions/priority", { params });
    }

}

export default new AppointmentsService();
